// @ts-strict-ignore
import {
	AfterViewInit,
	Component,
	DoCheck,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewChild,
	output,
} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {SimUtilityExtendedService} from "@services";
import {DynamicScriptDirective} from "../../../../../custom-directives/dynamic-script.directive";
import {Media} from "@models";

@Component({
	selector: "sim-pptx-viewer",
	templateUrl: "./pptx-viewer.component.html",
	styleUrls: ["./pptx-viewer.component.scss"],
	providers: [EventEmitterService],
	standalone: false,
})
export class PptxViewerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit, DoCheck {
	@Input() public media: Media = null;
	@Input() public previewMediaUrl: string = null;
	@Input() public file: File | string;
	@Input() public enableAutoPlay: boolean;
	@Input() public autoPlayInterval: number;
	@Input() public width = 1920;
	@Input() public height = 1080;
	@Input() public pageNum = 0;
	@Input() autoSize = false;

	numberOfPages = output<number>();
	loadingError = output<boolean>();
	loaded = output<boolean>();

	public slideJsInstance: any = null;
	public autoPlayIntervalHandler: any = null;
	private isVisible = false;

	private loadSkipped = false;
	@ViewChild("slidejs", {static: true}) slideJsScript: DynamicScriptDirective;

	@ViewChild("canvasContainer", {static: true})
	canvasContainer: ElementRef<HTMLDivElement>;
	@ViewChild("pptxCanvas", {static: true})
	pptxCanvas: ElementRef<HTMLDivElement>;

	constructor(
		private _simUtilityService: SimUtilityExtendedService,
		private _emitterService: EventEmitterService,
	) {}

	ngOnInit() {
		this.slideJsScript.onResult.then(() => {
			if (this.loadSkipped) {
				this.handleMediaLoad();
			}
		});

		this.updateDimensions();
	}

	public ngAfterViewInit() {
		this.slideJsInstance = this.createSlideJsFunc()?.();
	}

	public ngDoCheck() {
		let nextVisibility = this.pptxCanvas && this.pptxCanvas.nativeElement && this.pptxCanvas.nativeElement.offsetWidth > 0;

		if (this.isVisible !== nextVisibility) {
			this.isVisible = nextVisibility;

			if (this.isVisible) {
				this.handleMediaLoad();
			}
		}
	}

	private createSlideJsFunc(): () => any {
		// See: SIMSOFT-5184
		if (window["createSlideJS"]) {
			return window["createSlideJS"];
		} else if (window["SlideJS"]) {
			return () => new window["SlideJS"]();
		}
	}

	private async handleMediaLoad() {
		if (this.isVisible) {
			let pptxUrl = "";
			if (this.media) {
				pptxUrl = this._simUtilityService.getMediaPath(this.media);
			}

			if (this.slideJsInstance) {
				this.slideJsInstance.destroy();
			}
			this.slideJsInstance = this.createSlideJsFunc()?.();

			if (this.slideJsInstance) {
				await fetch(pptxUrl)
					.then((res) => res.blob()) // Gets the response and returns it as a blob
					.then((blob) => {
						const metadata = {
							type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
						};

						let pptxFile = this.media
							? new File([blob], this.media ? this.media?.fileName : "previewPowerPoint", metadata)
							: this.file;

						this.slideJsInstance.parse(
							pptxFile,
							() => {
								if (this.slideJsInstance.render) {
									// ============================================================== //
									// 2019-12-04, Alex                                               //
									// Use this method for the bought version of kukudocs             //
									// /\_/\                                                          //
									// (o.o)                                                          //
									// > ^ <                                                          //
									// ============================================================== //

									this.slideJsInstance.render(this.pptxCanvas.nativeElement, (result) => {
										if (result.isError) {
											console.warn(result.msg);
										} else {
											this.numberOfPages.emit(this.getPages());
											this.loaded.emit(true);

											// ----------------------------------------
											// Hide ui elements of slidejs viewer.
											// We will handle navigation and zoom via
											// api calls.
											// ----------------------------------------

											if (this.slideJsInstance.ui) {
												if (this.slideJsInstance.ui.desktopUI) {
													this.slideJsInstance.ui.desktopUI.destroy();
												}

												if (this.slideJsInstance.ui.mobileUI) {
													this.slideJsInstance.ui.mobileUI.destroy();
												}
											}

											// ----------------------------------------
											// Digital Signage
											// ----------------------------------------
											if (this.enableAutoPlay) {
												this.startAutoPlay();
											}
										}
									});
								} else if (window["slideAfterRender"]) {
									// ============================================================== //
									// 2019-12-04, Alex                                               //
									// Use this method for the free trial version of kukudocs         //
									// /\_/\                                                          //
									// (o.o)                                                          //
									// > ^ <                                                          //
									// ============================================================== //

									window["slideAfterRender"](
										this.pptxCanvas.nativeElement,
										(result) => {
											if (result.isError) {
												console.log("error displaying pptx");
											} else {
												// ----------------------------------------
												// Hide ui elements of slidejs viewer.
												// We will handle navigation and zoom via
												// api calls.
												// ----------------------------------------

												if (this.slideJsInstance.ui.desktopUI) {
													this.slideJsInstance.ui.desktopUI.destroy();
												}

												if (this.slideJsInstance.ui.mobileUI) {
													this.slideJsInstance.ui.mobileUI.destroy();
												}

												// ----------------------------------------
												// Digital Signage
												// ----------------------------------------
												if (this.enableAutoPlay) {
													this.startAutoPlay();
												}
											}
										},
										0,
									);
								} else {
									console.warn("Kukudocs has encountered an error.");
								}
							},
							(e) => {
								console.log(e);
								this.loadingError.emit(true);
							},
							null,
						);
					});
			} else {
				console.warn("Plugin for pptx was not found (Kukudocs).");
			}
		}
	}

	public async ngOnChanges(changes: SimpleChanges) {
		if (changes["media"]?.currentValue || changes["file"]?.currentValue) {
			if (this.slideJsInstance) {
				await this.handleMediaLoad();
				this.goToPageNum();
			} else {
				this.loadSkipped = true;
			}
		}

		if (changes["pageNum"]) {
			this.goToPageNum();
		}
	}

	public ngOnDestroy(): void {
		if (this.slideJsInstance && this.slideJsInstance.destroy) {
			this.slideJsInstance.destroy(() => {
				this.slideJsInstance = null;
			});
		}

		if (this.autoPlayIntervalHandler) {
			clearInterval(this.autoPlayIntervalHandler);
		}
	}

	private startAutoPlay() {
		if (this.getPages() > 0) {
			if (!this.autoPlayIntervalHandler) {
				this.autoPlayIntervalHandler = setInterval(() => {
					if (this.pageNum < this.getPages()) {
						this.nextPage();
					} else {
						this.firstPage();
					}
				}, this.autoPlayInterval);
			}
		}
	}

	private firstPage() {
		this.pageNum = 1;
		this.goToPageNum();
	}

	private lastPage() {
		this.pageNum = this.getPages();
		this.goToPageNum();
	}

	private prevPage() {
		if (this.pageNum > 1) {
			this.pageNum--;
			this.goToPageNum();
		}
	}

	private nextPage() {
		if (this.pageNum < this.getPages()) {
			this.pageNum++;
			this.goToPageNum();
		}
	}

	private getPages() {
		if (
			this.slideJsInstance &&
			this.slideJsInstance.loader &&
			this.slideJsInstance.loader.view &&
			this.slideJsInstance.loader.view.options
		) {
			return this.slideJsInstance.loader.view.options.totalPage;
		} else {
			return 0;
		}
	}

	private goToPageNum() {
		if (this.slideJsInstance) {
			this.slideJsInstance.gotoPage(this.pageNum - 1);
		}
	}

	private calculateDynamicSize(): {width: number; height: number} {
		const container = this.canvasContainer.nativeElement;
		return {width: container.clientWidth, height: container.clientHeight};
	}

	private updateDimensions() {
		const dimensions = this.calculateDynamicSize();
		this.width = dimensions.width;
		this.height = dimensions.height;
	}
}
